import Cookies from 'js-cookie';
import _ from 'underscore';

const languageParts = (specifier: string) => specifier.toLowerCase().split('-');

/*
 * Normalize locale values reported from the browser.
 * Accounts for some unexpected values reported from
 * navigator.language or navigator.languages:
 *
 * Safari: "en-us" (all lowercase)
 * Chrome: "en-SA@CALENDAR=GREGORIAN"
 * Chrome: "en-US.UTF-8"
 */
const normalizeLocale = (locale: string) => {
  let normalizedLocale = locale.split('@')[0];
  normalizedLocale = normalizedLocale.split('.')[0];
  const components = normalizedLocale.split(/[-_]/);
  if (components.length === 1) {
    return components[0];
  } else {
    const [lang, place, ...rest] = components;

    return [lang, place.toUpperCase(), ...rest].join('-');
  }
};

/*
 * Returns true if the locales are the same or if one of the
 * locales are a prefix of the other:
 *
 * "en-US" matches "en-US"
 * "en-US" matches "en"
 * "en" matches "en-US"
 * "en-US" does not match "en-GB"
 */
const matches = (localeA: string, localeB: string) => {
  let shorter = languageParts(normalizeLocale(localeA));
  let longer = languageParts(normalizeLocale(localeB));
  if (shorter.length > longer.length) {
    [shorter, longer] = [longer, shorter];
  }

  return shorter.every((part: string, index: number) => longer[index] === part);
};

/*
 * Get a list of preferred locales based on the user's browser
 * settings & preferences. May or may not align with window.locale
 * which is set by the HTML Webpack Plugin
 */
export const _getPreferredLanguages = () =>
  _.chain([
    Cookies.get('lang'), // Accept - Language echo
    navigator.language, // Safari, Chrome
    ...(navigator.languages || []), // Chrome, Firefox
    'en-GB', // Just in case
  ])
    .compact()
    .map((locale) => normalizeLocale(locale!))
    .unique()
    .value();

/*
 * Does the given locale match the locale set by the HTML
 * template in `window.locale`?
 */
const matchesHTMLTemplateDefaultLocale = (
  localeToTestAgainstWindowLocale: string,
) => {
  const htmlTemplateLocale = normalizeLocale(window.locale || 'en');

  return matches(htmlTemplateLocale, localeToTestAgainstWindowLocale);
};

/*
 * Does the given locale match the top preferred locale from the
 * user's browser settings
 */
const isPreferredLocale = (localeToTestAgainstTopPreferred: string) =>
  matches(_getPreferredLanguages()[0], localeToTestAgainstTopPreferred);

/*
 * Given an ordered list of preferred locales and a list of locales to test,
 * find the first match between the two as the "most preferred" option
 */
const mostPreferredLocale = (
  orderedLocalePreferences: string[],
  localesToTest: string[],
) =>
  _.chain(orderedLocalePreferences)
    .map((preferredLang: string) =>
      _.find(localesToTest, (lang) => matches(preferredLang, lang)),
    )
    .compact()
    .first()
    .value();

export default {
  preferred: _getPreferredLanguages(),
  matches,
  matchesLocale: matchesHTMLTemplateDefaultLocale,
  normalizeLocale,
  is: isPreferredLocale,
  mostPreferred: mostPreferredLocale,
};



// WEBPACK FOOTER //
// ./app/common/lib/language.ts